import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Progress,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppCard,
  AppDatePicker,
  AppInput,
  AppSelect,
  MasterAddressFields,
  OutlineButton,
  PhotoUploadField,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
} from '../../../components/ui';
import { sitesForBu } from '../../../data/master/businessUnits';
import {
  CONSTITUTION_OPTIONS,
  MAJOR_CATEGORY_OPTIONS,
} from '../../../data/master/vendors';
import {
  lookupVillages,
  lookupBusinessUnits,
  lookupSites,
} from '../../../repositories/lookup.repository';
import { uploadPublicVendorDocument } from '../../../repositories/document.repository';
import { registerPublicVendor, getPublicRegistrationStatus } from '../../../repositories/publicVendor.repository';
import { dbIdForCode } from '../../../utils/entityResolve';
import { composeFullName } from '../../../utils/personName';
import {
  formatAadhaarInput,
  validateVendorForm,
  VENDOR_FIELD_LIMITS,
} from '../../../utils/vendorValidation';

/** form field → document.docType stored in S3 / documents table */
const REGISTER_DOC_FIELDS = [
  { field: 'photo', docType: 'profile_photo' },
  { field: 'gstCertificate', docType: 'gst_certificate' },
  { field: 'businessOfficePhoto', docType: 'office_photo' },
  { field: 'aadhaarFront', docType: 'aadhaar_front' },
  { field: 'aadhaarBack', docType: 'aadhaar_back' },
  { field: 'panCard', docType: 'pan_card' },
];

const STEPS = [
  { id: 'business', title: 'Business', hint: 'Trade name, GST & category' },
  { id: 'owner', title: 'Owner', hint: 'Contact & profile photo' },
  { id: 'address', title: 'Address', hint: 'Village & PIN' },
  { id: 'kyc', title: 'Documents', hint: 'GST, Aadhaar & photos' },
  { id: 'done', title: 'Done', hint: 'Submitted for review' },
];

const emptyForm = {
  gstNumber: '',
  tradeAgencyName: '',
  constitution: 'Proprietorship',
  dateOfIncorporation: '',
  ownerName: '',
  fatherHusbandName: '',
  surname: '',
  mobile: '',
  email: '',
  address1: '',
  address2: '',
  villageId: '',
  pin: '',
  buId: '',
  siteId: '',
  majorCategory: '',
  photo: null,
  aadhaarNumber: '',
  pan: '',
  gstCertificate: null,
  businessOfficePhoto: null,
  aadhaarFront: null,
  aadhaarBack: null,
  panCard: null,
};

function stepFieldKeys(step) {
  if (step === 0) return ['buId', 'siteId', 'tradeAgencyName', 'gstNumber', 'constitution'];
  if (step === 1) return ['ownerName', 'mobile', 'email'];
  if (step === 2) return ['villageId', 'address1', 'pin'];
  if (step === 3) return ['aadhaarNumber'];
  return [];
}

export default function VendorRegisterPage() {
  const toast = useToast();
  const [villages, setVillages] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationDisabledReason, setRegistrationDisabledReason] = useState(
    'Vendor self registration is temporarily unavailable. Please contact Utkarsh staff.',
  );
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submittedId, setSubmittedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [villageRes, buRes, siteRes, status] = await Promise.all([
          lookupVillages(),
          lookupBusinessUnits(),
          lookupSites(),
          getPublicRegistrationStatus(),
        ]);
        if (cancelled) return;
        setVillages(villageRes || []);
        setBusinessUnits(buRes || []);
        setSites(siteRes || []);
        setRegistrationEnabled(status?.enabled !== false);
        if (status?.disabledReason) setRegistrationDisabledReason(String(status.disabledReason));
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load registration form',
            status: 'error',
            position: 'top',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const ownerFullName = composeFullName(
    form.ownerName,
    form.fatherHusbandName,
    form.surname,
  );

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst || b.buId,
        })),
    [businessUnits],
  );

  const sitesForSelectedBu = useMemo(
    () => sitesForBu(sites, form.buId),
    [sites, form.buId],
  );

  const siteOptions = useMemo(
    () =>
      sitesForSelectedBu.map((s) => ({
        value: s.siteId,
        label: s.siteName,
        description: s.projectOperation || s.siteId,
      })),
    [sitesForSelectedBu],
  );

  // Site only when this BU actually has sites (e.g. hide for AICTPL with none)
  const showSiteField = Boolean(form.buId && sitesForSelectedBu.length > 0);

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  const validateStep = () => {
    const all = validateVendorForm(form, {
      requireGst: true,
      requireSite: showSiteField,
    });
    const keys = stepFieldKeys(step).filter((k) => k !== 'siteId' || showSiteField);
    const stepErrors = {};
    keys.forEach((k) => {
      if (all[k]) stepErrors[k] = all[k];
    });
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast({
        title: 'Please fix highlighted fields',
        status: 'warning',
        position: 'top',
        duration: 3000,
      });
      return;
    }
    // Cap at Documents (index 3) — never jump to Done via Continue
    setStep((s) => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Multi-step form: Enter / accidental submit on earlier steps must NOT register.
    // Only Documents (step 3) may submit.
    if (step < 3) {
      handleNext();
      return;
    }
    if (step !== 3 || saving) return;

    const all = validateVendorForm(form, {
      requireGst: true,
      requireSite: showSiteField,
    });
    setErrors(all);
    if (Object.keys(all).length) {
      toast({
        title: 'Please fix highlighted fields',
        status: 'warning',
        position: 'top',
      });
      return;
    }

    const composed = composeFullName(
      form.ownerName,
      form.fatherHusbandName,
      form.surname,
    );
    setSaving(true);
    try {
      const created = await registerPublicVendor({
        gstNumber: form.gstNumber.trim().toUpperCase(),
        tradeAgencyName: form.tradeAgencyName.trim(),
        constitution: form.constitution,
        dateOfIncorporation: form.dateOfIncorporation,
        ownerName: form.ownerName.trim(),
        fatherHusbandName: form.fatherHusbandName.trim(),
        surname: form.surname.trim(),
        ownerFullName: composed,
        mobile: form.mobile.trim(),
        email: form.email.trim().toLowerCase(),
        address1: form.address1.trim(),
        address2: form.address2.trim(),
        villageId: form.villageId,
        villageDbId: dbIdForCode(villages, form.villageId, { codeKey: 'villageId' }),
        pin: form.pin.trim(),
        buId: form.buId,
        buDbId: dbIdForCode(businessUnits, form.buId, { codeKey: 'buId' }),
        siteId: form.siteId,
        siteDbId: dbIdForCode(sites, form.siteId, { codeKey: 'siteId' }),
        majorCategory: form.majorCategory || '',
        verificationStatus: 'Pending',
        active: true,
      });

      const uploads = REGISTER_DOC_FIELDS.map(({ field, docType }) => {
        const file = form[field]?.file;
        if (!file || !created.uploadToken) return null;
        return uploadPublicVendorDocument({
          uploadToken: created.uploadToken,
          docType,
          file,
        });
      }).filter(Boolean);

      if (uploads.length) {
        const results = await Promise.allSettled(uploads);
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed) {
          toast({
            title: `Registration saved, but ${failed} document(s) failed to upload`,
            status: 'warning',
            position: 'top',
            duration: 5000,
          });
        }
      }

      setSubmittedId(created.vendorId || created.code || null);
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({
        title: err?.message || 'Registration failed',
        status: 'error',
        position: 'top',
      });
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (!registrationEnabled) {
    return (
      <AppCard>
        <StatusAlertCard
          variant="error"
          title="Registration closed"
          body={registrationDisabledReason}
        />
      </AppCard>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack align="stretch" spacing={5}>
        <Box>
          <Heading size={{ base: 'lg', md: 'xl' }} mb={2}>
            Register as vendor
          </Heading>
          <Text color="text.secondary" fontSize={{ base: '14px', md: '15px' }}>
            Fill your business details. Our staff will verify your application before you can
            operate on Utkarsh.
          </Text>
        </Box>

        {step < 4 ? (
          <AppCard p={{ base: 4, md: 5 }}>
            <Flex justify="space-between" align="center" mb={2} gap={2} wrap="wrap">
              <Text fontSize="13px" fontWeight="600" color="text.secondary">
                Step {step + 1} of {STEPS.length - 1} · {STEPS[step].title}
              </Text>
              <StatusBadge status="pending">{STEPS[step].hint}</StatusBadge>
            </Flex>
            <Progress
              value={progress}
              size="sm"
              borderRadius="full"
              colorScheme="yellow"
              bg="bg.input"
              mb={5}
            />

            {step === 0 ? (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppSelect
                    label="Business Unit"
                    isRequired
                    isSearchable
                    placeholder="Select business unit"
                    value={form.buId}
                    onChange={(buId) => patch({ buId: buId || '', siteId: '' })}
                    options={buOptions}
                    isInvalid={!!errors.buId}
                    errorMessage={errors.buId}
                  />
                  {showSiteField ? (
                    <AppSelect
                      label="Site"
                      isRequired
                      isSearchable
                      placeholder="Select site"
                      value={form.siteId}
                      onChange={(siteId) => patch({ siteId: siteId || '' })}
                      options={siteOptions}
                      isInvalid={!!errors.siteId}
                      errorMessage={errors.siteId}
                    />
                  ) : null}
                </SimpleGrid>
                <AppInput
                  label="Trade / Agency Name"
                  isRequired
                  value={form.tradeAgencyName}
                  onChange={(e) => patch({ tradeAgencyName: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.tradeAgencyName.max}
                  isInvalid={!!errors.tradeAgencyName}
                  errorMessage={errors.tradeAgencyName}
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppInput
                    label="GST Number"
                    isRequired
                    value={form.gstNumber}
                    onChange={(e) =>
                      patch({ gstNumber: e.target.value.toUpperCase().slice(0, 15) })
                    }
                    placeholder="15-character GSTIN"
                    maxLength={15}
                    isInvalid={!!errors.gstNumber}
                    errorMessage={errors.gstNumber}
                  />
                  <AppSelect
                    label="Constitution of Business"
                    isRequired
                    value={form.constitution}
                    onChange={(v) => patch({ constitution: v })}
                    options={CONSTITUTION_OPTIONS.map((c) => ({ value: c, label: c }))}
                    isInvalid={!!errors.constitution}
                    errorMessage={errors.constitution}
                  />
                </SimpleGrid>
                <AppDatePicker
                  label="Date of Incorporation"
                  value={form.dateOfIncorporation}
                  onChange={(e) => patch({ dateOfIncorporation: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  isInvalid={!!errors.dateOfIncorporation}
                  errorMessage={errors.dateOfIncorporation}
                  helperText="Optional · type DD/MM/YYYY (/ added automatically)"
                />
                <AppSelect
                  label="Major Category"
                  value={form.majorCategory}
                  onChange={(v) => patch({ majorCategory: v || '' })}
                  options={[{ value: '', label: 'Select…' }, ...MAJOR_CATEGORY_OPTIONS]}
                />
              </VStack>
            ) : null}

            {step === 1 ? (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppInput
                    label="Owner / Partner Name"
                    isRequired
                    value={form.ownerName}
                    onChange={(e) => patch({ ownerName: e.target.value })}
                    isInvalid={!!errors.ownerName}
                    errorMessage={errors.ownerName}
                  />
                  <AppInput
                    label="Father’s / Husband’s Name"
                    value={form.fatherHusbandName}
                    onChange={(e) => patch({ fatherHusbandName: e.target.value })}
                  />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppInput
                    label="Surname"
                    value={form.surname}
                    onChange={(e) => patch({ surname: e.target.value })}
                  />
                  <AppInput
                    label="Full Name"
                    value={ownerFullName}
                    isReadOnly
                    placeholder="Auto from name parts"
                  />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppInput
                    label="Mobile Number"
                    isRequired
                    leftAddon="+91"
                    value={form.mobile}
                    onChange={(e) =>
                      patch({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
                    }
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    isInvalid={!!errors.mobile}
                    errorMessage={errors.mobile}
                  />
                  <AppInput
                    label="Email"
                    type="email"
                    isRequired
                    value={form.email}
                    onChange={(e) => patch({ email: e.target.value })}
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                  />
                </SimpleGrid>
                <PhotoUploadField
                  label="Profile / Logo"
                  value={form.photo}
                  onChange={(photo) => patch({ photo })}
                />
              </VStack>
            ) : null}

            {step === 2 ? (
              <MasterAddressFields
                villages={villages}
                address1={form.address1}
                address2={form.address2}
                villageId={form.villageId}
                pin={form.pin}
                onChange={patch}
                errors={errors}
              />
            ) : null}

            {step === 3 ? (
              <VStack align="stretch" spacing={4}>
                <Text fontSize="13px" color="text.secondary">
                  Upload clear photos or PDF scans. Files go to secure storage for staff review.
                </Text>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  <PhotoUploadField
                    label="GST certificate"
                    value={form.gstCertificate}
                    onChange={(gstCertificate) => patch({ gstCertificate })}
                  />
                  <PhotoUploadField
                    label="Business / office photo"
                    value={form.businessOfficePhoto}
                    onChange={(businessOfficePhoto) => patch({ businessOfficePhoto })}
                  />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  <AppInput
                    label="Aadhaar number"
                    value={form.aadhaarNumber}
                    onChange={(e) => patch({ aadhaarNumber: formatAadhaarInput(e.target.value) })}
                    placeholder="XXXX-XXXX-XXXX"
                    inputMode="numeric"
                    maxLength={14}
                    isInvalid={!!errors.aadhaarNumber}
                    errorMessage={errors.aadhaarNumber}
                  />
                  <AppInput
                    label="PAN (optional)"
                    value={form.pan}
                    onChange={(e) =>
                      patch({ pan: e.target.value.toUpperCase().slice(0, 10) })
                    }
                    isInvalid={!!errors.pan}
                    errorMessage={errors.pan}
                  />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                  <PhotoUploadField
                    label="Aadhaar — Front"
                    value={form.aadhaarFront}
                    onChange={(aadhaarFront) => patch({ aadhaarFront })}
                  />
                  <PhotoUploadField
                    label="Aadhaar — Back"
                    value={form.aadhaarBack}
                    onChange={(aadhaarBack) => patch({ aadhaarBack })}
                  />
                  <PhotoUploadField
                    label="PAN card (optional)"
                    value={form.panCard}
                    onChange={(panCard) => patch({ panCard })}
                  />
                </SimpleGrid>
              </VStack>
            ) : null}

            <Flex
              mt={6}
              gap={3}
              direction={{ base: 'column-reverse', sm: 'row' }}
              justify="space-between"
            >
              <OutlineButton
                type="button"
                w={{ base: '100%', sm: 'auto' }}
                onClick={handleBack}
                isDisabled={step === 0}
              >
                Back
              </OutlineButton>
              {step < 3 ? (
                <PrimaryButton
                  type="button"
                  w={{ base: '100%', sm: 'auto' }}
                  onClick={handleNext}
                >
                  Continue
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="button"
                  w={{ base: '100%', sm: 'auto' }}
                  isLoading={saving}
                  onClick={handleSubmit}
                >
                  Submit for verification
                </PrimaryButton>
              )}
            </Flex>
          </AppCard>
        ) : (
          <AppCard p={{ base: 5, md: 8 }} textAlign="center">
            <StatusBadge status="pending" mb={4}>
              Pending verification
            </StatusBadge>
            <Heading size="md" mb={3}>
              Application submitted
            </Heading>
            <Text color="text.secondary" mb={2}>
              Reference ID:{' '}
              <Text as="span" fontFamily="mono" fontWeight="700" color="text.primary">
                {submittedId}
              </Text>
            </Text>
            <Text color="text.secondary" fontSize="15px" maxW="480px" mx="auto">
              Thank you, {form.tradeAgencyName || 'vendor'}. Our staff will review your details
              and documents. You will be contacted on {form.mobile || 'your mobile'} once
              verified.
            </Text>
          </AppCard>
        )}
      </VStack>
    </Box>
  );
}
